import { apiFetch } from './client.js';

export type FeeType =
  | { kind: 'FLAT'; amountCents: string }
  | { kind: 'PERCENTAGE'; basisPoints: number }
  | { kind: 'TIER'; tiers: FeeTier[] };

export interface FeeTier {
  minAmountCents: string;
  maxAmountCents?: string;
  fee: FeeType;
}

export interface FeeSchedule {
  id: string;
  name: string;
  version: number;
  currency: string;
  feeType: FeeType;
  effectiveFrom: string;
  /** Whether this price is being charged right now, or is not yet, or no longer. */
  state?: 'SCHEDULED' | 'IN_FORCE' | 'ENDED';
  effectiveTo?: string;
  createdAt: string;
  createdBy: string;
  /** DEV-142: this version published that earlier version's price again. */
  restoredFromVersion?: number;
}

export interface CreateFeeScheduleBody {
  name: string;
  currency: string;
  feeType: FeeType;
  effectiveFrom: string;
}

export interface FeePreviewBody {
  feeType: FeeType;
  currency: string;
  testAmountsCents: string[];
}

export interface FeePreviewResult {
  amountCents: string;
  feeCents: string;
}

export interface FeeDiffRow {
  parameter: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface FeeDiffResult {
  scheduleId: string;
  fromVersion: number;
  toVersion: number;
  rows: FeeDiffRow[];
}

/** DEV-127: what a provider (GuruPay) charges us. Estimates only. */
export interface ProviderFeeSchedule {
  id: string;
  provider: string;
  operation: string;
  currency: string;
  version: number;
  feeType: FeeType;
  effectiveFrom: string;
  effectiveTo?: string;
  createdAt: string;
  createdBy: string;
  state: 'SCHEDULED' | 'IN_FORCE' | 'ENDED';
}

export interface CreateProviderFeeBody {
  provider: string;
  operation: string;
  currency: string;
  feeType: FeeType;
  effectiveFrom: string;
  reason: string;
}

export interface ProviderFeeEstimate {
  basis: 'ESTIMATE';
  scheduleId: string;
  version: number;
  amountCents: string;
  estimatedFeeCents: string;
  at: string;
}

export const PROVIDER_FEE_OPERATIONS = [
  'SEPA_OUT',
  'SEPA_IN',
  'SEPA_INSTANT_OUT',
  'SWIFT_OUT',
  'SWIFT_IN',
  'INTERNAL_TRANSFER',
  'MONTHLY',
] as const;

export const providerFeesApi = {
  list: (provider = 'GURUPAY'): Promise<ProviderFeeSchedule[]> =>
    apiFetch<ProviderFeeSchedule[]>(
      `/admin/provider-fees/schedules?provider=${encodeURIComponent(provider)}`,
    ),

  create: (body: CreateProviderFeeBody): Promise<ProviderFeeSchedule> =>
    apiFetch<ProviderFeeSchedule>('/admin/provider-fees/schedules', { method: 'POST', body }),

  estimate: (params: {
    provider: string;
    operation: string;
    currency: string;
    amountCents: string;
  }): Promise<ProviderFeeEstimate> =>
    apiFetch<ProviderFeeEstimate>(
      `/admin/provider-fees/estimate?${new URLSearchParams(params).toString()}`,
    ),
};

export const feesApi = {
  listSchedules: (
    params: { currency?: string; active?: boolean; namePattern?: string } = {},
  ): Promise<FeeSchedule[]> => {
    const qs = new URLSearchParams();
    if (params.currency) qs.set('currency', params.currency);
    if (params.active !== undefined) qs.set('active', String(params.active));
    if (params.namePattern) qs.set('namePattern', params.namePattern);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<FeeSchedule[]>(`/admin/fees/schedules${suffix}`);
  },

  getSchedule: (scheduleId: string): Promise<FeeSchedule> =>
    apiFetch<FeeSchedule>(`/admin/fees/schedules/${scheduleId}`),

  createSchedule: (body: CreateFeeScheduleBody): Promise<FeeSchedule> =>
    apiFetch<FeeSchedule>('/admin/fees/schedules', { method: 'POST', body }),

  deactivateSchedule: (scheduleId: string, reason: string): Promise<FeeSchedule> =>
    apiFetch<FeeSchedule>(`/admin/fees/schedules/${scheduleId}/deactivate`, {
      method: 'POST',
      body: { reason },
    }),

  preview: (body: FeePreviewBody): Promise<FeePreviewResult[]> =>
    apiFetch<FeePreviewResult[]>('/admin/fees/schedules/preview', {
      method: 'POST',
      body,
    }),

  diff: (scheduleId: string, versionId: string): Promise<FeeDiffResult> =>
    apiFetch<FeeDiffResult>(
      `/admin/fees/schedules/${scheduleId}/diff?versionId=${encodeURIComponent(versionId)}`,
    ),
};
